import { Injectable } from '@angular/core';
import { Appointment } from '../../domain/models/appointment.model';
import { AppointmentCreateInput } from '../../domain/models/appointment-create-input.model';
import { AppointmentStatus } from '../../domain/models/appointment-status.model';
import { Department } from '../../domain/models/department.model';
import { Professional } from '../../domain/models/professional.model';
import { ReasonType } from '../../domain/models/reason-type.model';
import { Shift } from '../../domain/models/shift.model';
import { AppointmentDto } from '../dtos/appointment.dto';
import { DepartmentDto } from '../dtos/department.dto';
import { ExternalResourceDto } from '../dtos/external-resource.dto';
import { ProfessionalDto } from '../dtos/professional.dto';
import { ShiftDto } from '../dtos/shift.dto';

@Injectable()
export class AppointmentMapper {
  public toAppointment(dto: AppointmentDto): Appointment {
    return {
      id: dto.ExternalId,
      clientId: dto.ExternalClientId ?? null,
      patientIds: dto.ExternalPatientIds ?? [],
      professionalId: dto.ExternalProfessionalId ?? null,
      start: dto.Start ?? null,
      end: dto.End ?? null,
      complaint: dto.Complaint ?? null,
      status: this.resolveStatus(dto.Status),
      type: dto.Type ?? null,
      departmentId: dto.DepartmentId ?? null,
      reasonTypeId: dto.ReasonTypeId ?? null,
      title: dto.Title ?? null,
      notes: dto.Notes ?? null,
    };
  }

  public toProfessional(dto: ProfessionalDto): Professional {
    return {
      id: dto.ExternalId,
      firstName: dto.FirstName ?? null,
      lastName: dto.LastName ?? null,
      displayName: dto.DisplayName ?? null,
      email: dto.Email ?? null,
      isActive: dto.IsActive,
      isVirtual: dto.VirtualUser ?? false,
      userType: dto.UserType,
      avatarUrl: dto.PictureUrl ?? null,
      departmentId: dto.DepartmentId ?? null,
    };
  }

  public toReasonType(dto: ExternalResourceDto): ReasonType {
    return {
      id: dto.ExternalId,
      displayName: dto.DisplayName ?? null,
      color: this.resolveReasonTypeColor(dto.Attributes),
      durationMinutes: this.resolveReasonTypeDurationMinutes(dto.Attributes),
    };
  }

  // Returns null when ExternalId is missing (a department not yet linked to a ProVet record) --
  // filtered out by the caller, mirroring toShift's own null-and-filter pattern below, since
  // ProVet requires an external department id on appointment creation.
  public toDepartment(dto: DepartmentDto): Department | null {
    if (!dto.ExternalId) return null;
    return {
      id: dto.ExternalId,
      localId: dto.Id ?? null,
      displayName: dto.Name ?? null,
    };
  }

  // ProfessionalId/Start/End are nullable on the wire (untyped ProVet shift fields) -- a shift
  // missing any of them can't be attributed to a day/professional, so it's dropped by the caller.
  public toShift(dto: ShiftDto): Shift | null {
    if (!dto.ProfessionalId || !dto.Start || !dto.End) {
      return null;
    }
    return {
      professionalId: dto.ProfessionalId,
      start: dto.Start,
      end: dto.End,
    };
  }

  // Isolated on purpose: this is the ONLY place that builds the raw JsonObject body the
  // backend forwards to ProVet Cloud (AppointmentsController.CreateAppointment ->
  // ProVetProvider.ApplyAppointmentHyperlinks). Sends BARE external ids for client/patients/
  // user/reason/department -- the backend is what knows this tenant's ProVet host, so it (not
  // the frontend) turns them into fully-qualified hyperlinks like ".../client/2/". Field names
  // (`client`, `patients`, `user`, `reason`, `department`, `start`, `end`, `complaint`) were
  // confirmed against live ProVet appointment data; `type` is a separate, unrelated small int
  // enum the backend defaults on its own (NOT the appointment's reason/category).
  public toCreatePayload(input: AppointmentCreateInput): Record<string, unknown> {
    return this.toPayload(input);
  }

  // Mirrors toCreatePayload's field names -- same ProVet hyperlink fields, just sent through the
  // generic AppointmentsRemoteDatasource.update (PATCH) instead of create (POST).
  public toUpdatePayload(input: AppointmentCreateInput): Record<string, unknown> {
    return this.toPayload(input);
  }

  private toPayload(input: AppointmentCreateInput): Record<string, unknown> {
    // ProVet never accepts a patient without its owning client, and rejects an empty-string
    // `client`/`patients` value outright -- omit both keys entirely (rather than sending ""/[""])
    // for the internal-block use case where staff save the appointment with no client/patient.
    const patientIds = input.patientIds?.filter(Boolean) ?? [];

    return {
      ...(input.clientId ? { client: input.clientId } : {}),
      ...(input.clientId && patientIds.length ? { patients: patientIds } : {}),
      user: input.professionalId,
      department: input.departmentId || undefined,
      start: input.start,
      end: input.end,
      // ProVet requires `complaint` to be non-blank; the form only collects `title`, so mirror
      // it into `complaint` when the caller hasn't supplied one explicitly.
      complaint: input.complaint ?? input.title ?? '',
      reason: input.reasonTypeId || undefined,
      title: input.title,
      notes: input.notes ?? '',
    };
  }

  // Falls back to Programada for a missing status AND for the retired `1` (Confirmada), which no
  // longer matches any member of the enum -- see appointment-status.model.ts.
  private resolveStatus(value: number | null): AppointmentStatus {
    if (value == null || !(value in AppointmentStatus)) return AppointmentStatus.Programada;
    return value as AppointmentStatus;
  }

  // ProVet's exact casing for the reason-type color attribute isn't confirmed, so this does a
  // case-insensitive lookup across the raw Attributes bag and defensively normalizes the value:
  // trims whitespace, and prefixes bare 6/8-digit hex values (e.g. "3a64e3") with "#" since some
  // providers omit it. Anything empty or unparseable resolves to null so the UI can fall back to
  // a neutral accent instead of rendering an invalid CSS color.
  private resolveReasonTypeColor(attributes: Readonly<Record<string, string | null>> | null | undefined): string | null {
    if (!attributes) return null;

    const key = Object.keys(attributes).find((attributeKey) => attributeKey.toLowerCase() === 'color');
    const raw = key ? attributes[key] : null;
    if (!raw) return null;

    const trimmed = raw.trim();
    if (!trimmed) return null;

    const bareHexPattern = /^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
    if (bareHexPattern.test(trimmed)) return `#${trimmed}`;

    return trimmed;
  }

  // ProVet's exact casing for the reason-type duration attribute isn't confirmed either, so this
  // mirrors resolveReasonTypeColor's case-insensitive lookup. Non-numeric or non-positive values
  // resolve to null so the UI simply skips the end-time suggestion instead of applying a bogus one.
  private resolveReasonTypeDurationMinutes(attributes: Readonly<Record<string, string | null>> | null | undefined): number | null {
    if (!attributes) return null;

    const key = Object.keys(attributes).find((attributeKey) => attributeKey.toLowerCase() === 'duration');
    const raw = key ? attributes[key] : null;
    if (!raw) return null;

    const minutes = Number(raw);
    if (Number.isNaN(minutes) || minutes <= 0) return null;

    return minutes;
  }
}
